import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  GraduationCap,
  Loader2,
  Search,
  X,
  BookOpen,
  CheckCircle2,
  Clock,
  FileText,
  MessageSquare,
  Sparkles,
  ExternalLink,
  Pencil,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { useToast, useConfirm } from '../../Components/Feedback';
import { Card, Select, Modal, Badge, Button } from '../../Components/ui';
import AlunoBadges from '../../Components/AlunoBadges';
import PlanejamentoGradeModal from './PlanejamentoGradeModal';
import { corDaAtividade } from '../../utils/corDaAtividade';
import { corCardNivel } from '../../utils/niveis';
import { turnoParaPeriodo } from '../../utils/periodo';
import { API_URL } from '../../api/config';
import { useApi } from '../../api/useApi';

const DIAS_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const HORARIOS = ['HR 1', 'HR 2', 'HR 3'];
const TURNOS = ['Manhã', 'Tarde', 'Noite'];

// Tamanho padrão dos cards de turma nas duas grades semanais desta tela (a
// principal, do professor, e o modal de escala do aluno) — sem isso, cada
// coluna de dia encolhe pro conteúdo dela (nome de turma longo vs. "—"
// vazio), fazendo o card espremer o texto de forma diferente em cada tela/
// resolução. Largura mínima na COLUNA (força o scroll horizontal do
// wrapper em vez de espremer) e altura mínima no CARD (1 ou 2 linhas de
// texto ocupam o mesmo espaço).
const LARGURA_COLUNA_DIA = 'min-w-[132px]';
const ALTURA_CARD_TURMA = 'min-h-[44px]';

const ORDEM_PERIODOS = ['manha', 'tarde', 'noite'];
const LABEL_PERIODO = { manha: 'Manhã', tarde: 'Tarde', noite: 'Noite' };

// Data de hoje em YYYY-MM-DD no fuso LOCAL do navegador (mesmo cálculo do
// App.js) — new Date().toISOString() sozinho usa UTC, o que erraria o dia perto
// da meia-noite.
const getTodayString = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const date = new Date(d.getTime() - offset * 60 * 1000);
  return date.toISOString().split('T')[0];
};

// Normaliza pra busca (minúsculas, sem acento) — mesma função usada na busca
// manual de aluno da tela de Chamada (AttendanceList.jsx).
const normalizeString = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

// Data (YYYY-MM-DD, fuso local) do dia_semana de uma turma DENTRO DA SEMANA
// ATUAL — a Grade só mostra a semana corrente (Segunda-Sexta) de uma vez, sem
// navegação entre semanas, então "a mesma semana" é sempre em relação a hoje.
// Ex.: hoje quarta, turma de "Terça" → devolve a data de ontem, não a de hoje.
const dataDoDiaDaSemana = (hoje, diaSemana) => {
  const indice = DIAS_SEMANA.indexOf(diaSemana);
  if (indice === -1) return hoje;
  const dataHoje = new Date(`${hoje}T12:00:00`);
  const isoWeekdayHoje = dataHoje.getDay() === 0 ? 7 : dataHoje.getDay(); // 1=Segunda...7=Domingo
  const segundaFeira = new Date(dataHoje);
  segundaFeira.setDate(segundaFeira.getDate() - (isoWeekdayHoje - 1));
  const dataAlvo = new Date(segundaFeira);
  dataAlvo.setDate(dataAlvo.getDate() + indice);
  const offset = dataAlvo.getTimezoneOffset();
  return new Date(dataAlvo.getTime() - offset * 60000).toISOString().split('T')[0];
};

// Tela "Minha Grade": mostra as turmas da semana, organizadas por turno, e ao
// clicar numa turma mostra os alunos matriculados nela.
//
// Quando quem está logado tem uma conta de professor vinculada (ver
// usuarios.id_professor, vinculado em Admin Usuários), a tela já sabe quem ele
// é e mostra só as turmas DELE — sem seletor de professor nenhum, porque não
// tem necessidade de perguntar algo que o sistema já sabe. Master/coordenador/
// monitor continuam vendo um seletor pra escolher de quem quer ver a grade.
function Grade() {
  const { institutionId } = useInstitution();
  const { token, user } = useAuth();
  const { podeEditar } = usePermissao('/grade');
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const api = useApi();

  const [modalPlanejamentoAberto, setModalPlanejamentoAberto] = useState(false);
  const [abaModalTurma, setAbaModalTurma] = useState('inscritos'); // 'inscritos' | 'planejamento'
  const [editandoObsTurma, setEditandoObsTurma] = useState(false);
  const [textoObsTurma, setTextoObsTurma] = useState('');
  const [salvandoObsTurma, setSalvandoObsTurma] = useState(false);
  const [novoConteudoTurma, setNovoConteudoTurma] = useState('');
  const [salvandoNovoPlanoTurma, setSalvandoNovoPlanoTurma] = useState(false);
  // Chute inicial pelo relógio do aparelho, corrigido pela data do SERVIDOR
  // logo abaixo — mesmo padrão de App.js/Chamada (GET /api/hoje). Essa data
  // decide o status "hoje"/"passado"/"futuro" de cada turma da semana, não
  // pode ficar presa ao relógio de quem está olhando a tela.
  const [hoje, setHoje] = useState(() => getTodayString());

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/hoje`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.hoje) setHoje(data.hoje);
      })
      .catch(() => {});
  }, [token]);

  const authHeaders = useMemo(
    () => ({
      'x-institution-id': institutionId,
      Authorization: `Bearer ${token}`,
    }),
    [institutionId, token],
  );

  const souProfessor = user?.perfil === 'professor' && !!user?.id_professor;
  // Perfil professor mas SEM vínculo cadastrado ainda (ver Admin Usuários) —
  // caso diferente de souProfessor: aqui a tela não tem como saber quais
  // turmas mostrar, então nem tenta, só avisa.
  const professorSemVinculo = user?.perfil === 'professor' && !user?.id_professor;

  const [turnoSelecionado, setTurnoSelecionado] = useState('Manhã');
  const [professorFiltro, setProfessorFiltro] = useState('');
  const [turmaSelecionada, setTurmaSelecionada] = useState(null);
  const [alunoSelecionado, setAlunoSelecionado] = useState(null); // { id, nome } — abre o modal de escala semanal
  const [buscaAluno, setBuscaAluno] = useState('');

  // Lista completa de alunos da instituição, carregada uma vez só, pra busca
  // local instantânea — mesmo padrão da busca manual da tela de Chamada
  // (AttendanceList.jsx).
  const { data: todosAlunos = [] } = useQuery({
    queryKey: ['todos-alunos-grade', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/alunos?status=todos`, { headers: authHeaders });
      if (!res.ok) throw new Error('Erro ao carregar alunos');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!institutionId && !!token,
  });

  const resultadosBuscaAluno = useMemo(() => {
    if (!buscaAluno.trim()) return [];
    const termo = normalizeString(buscaAluno);
    return todosAlunos.filter((a) => normalizeString(a.nome).includes(termo)).slice(0, 10);
  }, [buscaAluno, todosAlunos]);

  const { data: atividades = [], isLoading } = useQuery({
    queryKey: ['atividades-grade', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/atividades`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
    // Essa mesma grade pode ter sido alterada em outra tela (Ajuste de Grade,
    // Grade por Turma) que usa sua própria chave de cache — invalidateQueries
    // de lá não alcança essa aqui. refetchOnMount 'always' ignora o staleTime
    // padrão (5min, ver App.js) e busca de novo toda vez que a tela é aberta,
    // sem bloquear a exibição do que já estava em cache.
    refetchOnMount: 'always',
    // Cobre o caso da tela já estar aberta numa aba em segundo plano enquanto
    // a alteração é feita em outra — refetchOnWindowFocus 'always' busca de
    // novo ao voltar o foco pra essa aba, sem esperar o staleTime.
    refetchOnWindowFocus: 'always',
  });

  // Se é professor vinculado, o filtro é sempre ele mesmo — nem aparece opção
  // de trocar. Senão, usa o que foi escolhido no seletor (ou nenhum = todos).
  const idProfessorEfetivo = souProfessor
    ? user.id_professor
    : professorFiltro
      ? Number(professorFiltro)
      : null;

  // Inclui tanto o professor principal (idprofessor) quanto os co-docentes
  // (professores_adicionais, ver seção "co-docência" em Turmas.js e
  // atividade_professores no backend) — sem isso, um co-professor nem
  // aparecia como opção pro master/coordenador escolher aqui.
  const professoresDisponiveis = useMemo(() => {
    const mapa = new Map();
    atividades.forEach((a) => {
      if (a.idprofessor && a.nome_professor && !mapa.has(a.idprofessor)) {
        mapa.set(a.idprofessor, a.nome_professor);
      }
      (a.professores_adicionais || []).forEach((p) => {
        if (!mapa.has(p.id)) mapa.set(p.id, p.nome);
      });
    });
    return Array.from(mapa.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [atividades]);

  // Só turmas ativas (não encerradas), do turno selecionado, e do professor
  // efetivo (se houver um definido) — bate tanto no principal (idprofessor)
  // quanto em qualquer co-docente (professores_adicionais). Sem essa segunda
  // checagem, um co-professor logado nunca via a turma na própria grade dele,
  // mesmo já podendo bater ponto e lançar nota nela (backend/pontos.js e
  // backend/notas.js já tratavam co-docência certo; só esta tela ficou de fora).
  const turmasDoTurno = useMemo(() => {
    return atividades.filter(
      (a) =>
        a.dia_semana &&
        a.horario &&
        a.turno === turnoSelecionado &&
        !a.data_fim &&
        (!idProfessorEfetivo ||
          a.idprofessor === idProfessorEfetivo ||
          (a.professores_adicionais || []).some((p) => p.id === idProfessorEfetivo)),
    );
  }, [atividades, turnoSelecionado, idProfessorEfetivo]);

  const grade = useMemo(() => {
    const grid = {};
    DIAS_SEMANA.forEach((dia) => {
      grid[dia] = {};
      HORARIOS.forEach((horario) => {
        grid[dia][horario] = [];
      });
    });
    turmasDoTurno.forEach((t) => {
      const dia = (t.dia_semana || '').trim();
      const horario = (t.horario || '').trim();
      if (grid[dia] && grid[dia][horario]) grid[dia][horario].push(t);
    });
    return grid;
  }, [turmasDoTurno]);

  // HR1/HR2 sempre aparecem (são os horários padrão, mesmo com a linha toda
  // vazia) — HR3 só entra se alguma turma de verdade usar esse horário nesta
  // grade, pra não deixar uma linha inteira de "—" só de enfeite.
  const horariosVisiveis = useMemo(
    () =>
      HORARIOS.filter(
        (h) => h !== 'HR 3' || DIAS_SEMANA.some((dia) => (grade[dia]?.[h] || []).length > 0),
      ),
    [grade],
  );

  const { data: roster = [], isLoading: carregandoRoster } = useQuery({
    queryKey: ['roster-turma-grade', turmaSelecionada?.id],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/api/matriculas/por-instituicao?id_atividade=${turmaSelecionada.id}`,
        { headers: authHeaders },
      );
      if (!res.ok) throw new Error('Erro ao carregar alunos');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!turmaSelecionada,
    refetchOnMount: 'always', // sempre atualizado ao abrir o card da turma, não só na primeira vez
    refetchOnWindowFocus: 'always',
  });

  // Data real (dentro da semana atual) do dia_semana da turma aberta no modal,
  // e se essa data já passou, é hoje, ou ainda vai acontecer — decide o que a
  // lista de presença abaixo mostra (ver comentário no botão/badge de status).
  const dataTurmaSelecionada = useMemo(
    () => (turmaSelecionada ? dataDoDiaDaSemana(hoje, turmaSelecionada.dia_semana.trim()) : null),
    [turmaSelecionada, hoje],
  );
  const modoDiaSelecionado = useMemo(() => {
    if (!dataTurmaSelecionada || dataTurmaSelecionada === hoje) return 'hoje';
    return dataTurmaSelecionada < hoje ? 'passado' : 'futuro';
  }, [dataTurmaSelecionada, hoje]);

  // Presença do dia sendo olhado no modal (hoje, ou um dia passado da mesma
  // semana) — mesma rota que monta a Chamada principal (ignoreFilters=true
  // pra trazer o status de qualquer aluno). Só busca quando um card é aberto
  // (não mais sempre, pra instituição inteira, como antes) e nunca pra um dia
  // futuro (não tem o que buscar — a chamada ainda nem aconteceu).
  // `turno` manda o backend juntar só a presença DO PERÍODO dessa turma (ver
  // comentário em backend/alunos.js, rota /por-dia) — sem isso, um aluno
  // matriculado também numa turma de outro turno no mesmo dia (ex.: turma
  // comum de manhã + ensaio à noite) podia aparecer aqui com a presença do
  // turno ERRADO, porque a consulta sem esse filtro trazia as duas linhas de
  // presença do aluno (uma por período) e a última a chegar "ganhava".
  const { data: presencasDoDia = [] } = useQuery({
    queryKey: ['presencas-dia-grade', institutionId, dataTurmaSelecionada, turmaSelecionada?.turno],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/api/alunos/por-dia?data=${dataTurmaSelecionada}&ignoreFilters=true&turno=${encodeURIComponent(turmaSelecionada.turno)}`,
        { headers: authHeaders },
      );
      if (!res.ok) throw new Error('Erro ao carregar presença do dia');
      const body = await res.json();
      const payload = body?.data ?? body;
      return Array.isArray(payload) ? payload : payload?.alunos || [];
    },
    enabled: !!institutionId && !!token && !!turmaSelecionada && modoDiaSelecionado !== 'futuro',
  });

  // Planejamento semanal da turma aberta no modal
  const {
    data: planTurmaData,
    isLoading: carregandoPlanTurma,
    refetch: recarregarPlanTurma,
  } = useQuery({
    queryKey: [
      'planejamento-turma-grade',
      institutionId,
      turmaSelecionada?.nome,
      dataTurmaSelecionada,
      turmaSelecionada?.idprofessor,
      idProfessorEfetivo,
    ],
    queryFn: async () => {
      const profId = turmaSelecionada?.idprofessor || idProfessorEfetivo || '';
      const res = await api.get(
        `/api/planejamento/semana-atual?data=${dataTurmaSelecionada}&nome_disciplina=${encodeURIComponent(turmaSelecionada.nome)}${profId ? `&id_professor=${profId}` : ''}`,
      );
      return res?.data ?? res;
    },
    enabled: !!institutionId && !!token && !!turmaSelecionada && !!dataTurmaSelecionada,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (planTurmaData?.planejamento) {
      setTextoObsTurma(planTurmaData.planejamento.observacoes || '');
    } else {
      setTextoObsTurma('');
      setNovoConteudoTurma('');
    }
    setEditandoObsTurma(false);
  }, [planTurmaData]);

  const alternarStatusPlanejamento = async (plan) => {
    const novoStatus = plan.status === 'concluido' ? 'pendente' : 'concluido';
    try {
      await api.patch(`/api/planejamento/${plan.id}`, { status: novoStatus });
      toast.success(
        novoStatus === 'concluido'
          ? 'Planejamento marcado como feito!'
          : 'Planejamento reaberto como pendente.',
      );
      recarregarPlanTurma();
    } catch (err) {
      toast.error(err.message || 'Erro ao alterar status.');
    }
  };

  const salvarObsTurma = async (planId) => {
    setSalvandoObsTurma(true);
    try {
      await api.patch(`/api/planejamento/${planId}`, { observacoes: textoObsTurma });
      toast.success('Anotações salvas com sucesso!');
      setEditandoObsTurma(false);
      recarregarPlanTurma();
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar anotações.');
    } finally {
      setSalvandoObsTurma(false);
    }
  };

  const criarPlanejamentoTurma = async (e) => {
    e?.preventDefault();
    if (!novoConteudoTurma.trim()) {
      toast.error('Informe o conteúdo planejado.');
      return;
    }
    setSalvandoNovoPlanoTurma(true);
    try {
      await api.post('/api/planejamento', {
        nome_disciplina: turmaSelecionada.nome,
        data: dataTurmaSelecionada,
        conteudo: novoConteudoTurma,
        status: 'pendente',
        id_professor: turmaSelecionada.idprofessor || user?.id_professor || null,
      });
      toast.success('Planejamento semanal registrado!');
      setNovoConteudoTurma('');
      recarregarPlanTurma();
    } catch (err) {
      toast.error(err.message || 'Erro ao registrar planejamento.');
    } finally {
      setSalvandoNovoPlanoTurma(false);
    }
  };

  const presencaDoDiaPorAluno = useMemo(() => {
    const mapa = new Map();
    presencasDoDia.forEach((a) => mapa.set(a.id, a.presenca_status || null));
    return mapa;
  }, [presencasDoDia]);

  // Quantos inscritos da turma aberta no modal já estão marcados presentes no
  // dia sendo olhado — cruza o roster da turma com o mapa de presença acima
  // (mesmo usado botão a botão na lista abaixo), pra mostrar o resumo já na
  // linha do cabeçalho, sem precisar contar manualmente.
  const presentesNaTurma = useMemo(
    () => roster.filter((m) => presencaDoDiaPorAluno.get(m.aluno_id) === 'presente').length,
    [roster, presencaDoDiaPorAluno],
  );

  // Escala semanal do aluno clicado no modal de turma — todas as matrículas
  // ativas dele, não só a turma que estava sendo olhada, pra o professor ver o
  // resto da semana daquele aluno (ex.: pra saber se ele tem aula com outro
  // professor logo antes/depois).
  const { data: escalaAluno = [], isLoading: carregandoEscala } = useQuery({
    queryKey: ['escala-aluno-grade', alunoSelecionado?.id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/matriculas/aluno/${alunoSelecionado.id}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Erro ao carregar escala do inscrito');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!alunoSelecionado,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  // Mesmo formato de grade (dia x horário) da Grade Horária principal, pra usar
  // o mesmo visual de tabela no modal de escala do aluno.
  const escalaAlunoGrid = useMemo(() => {
    const grid = {};
    DIAS_SEMANA.forEach((dia) => {
      grid[dia] = {};
      HORARIOS.forEach((horario) => {
        grid[dia][horario] = [];
      });
    });
    escalaAluno.forEach((m) => {
      const dia = (m.dia_semana || '').trim();
      const horario = (m.horario || '').trim();
      if (grid[dia] && grid[dia][horario]) grid[dia][horario].push(m);
    });
    return grid;
  }, [escalaAluno]);

  // Mesma regra de horariosVisiveis acima, mas pra escala individual do
  // aluno — cada grade tem seus próprios dados, então HR3 pode estar em uso
  // numa e vazio na outra.
  const horariosVisiveisEscala = useMemo(
    () =>
      HORARIOS.filter(
        (h) =>
          h !== 'HR 3' || DIAS_SEMANA.some((dia) => (escalaAlunoGrid[dia]?.[h] || []).length > 0),
      ),
    [escalaAlunoGrid],
  );

  // Data real (dentro da semana atual) de cada dia útil, pra buscar/mapear a
  // presença do aluno selecionado dia a dia — mesmo cálculo de
  // dataTurmaSelecionada acima, só que pra semana inteira de uma vez.
  const datasDaSemana = useMemo(() => {
    const mapa = {};
    DIAS_SEMANA.forEach((dia) => {
      mapa[dia] = dataDoDiaDaSemana(hoje, dia);
    });
    return mapa;
  }, [hoje]);

  // Presença do aluno selecionado na semana sendo vista (Segunda a Sexta) —
  // mostrada no cabeçalho de cada dia do modal de escala, pra o professor ver
  // de cara como foi a semana dele sem precisar abrir cada turma separada.
  const { data: presencaSemanaAluno = [] } = useQuery({
    queryKey: [
      'presenca-semana-aluno',
      alunoSelecionado?.id,
      datasDaSemana['Segunda'],
      datasDaSemana['Sexta'],
    ],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/api/presenca?data_inicio=${datasDaSemana['Segunda']}&data_fim=${datasDaSemana['Sexta']}&aluno_id=${alunoSelecionado.id}`,
        { headers: authHeaders },
      );
      if (!res.ok) throw new Error('Erro ao carregar presença da semana');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!alunoSelecionado,
  });

  // Períodos (manhã/tarde/noite) que o aluno REALMENTE tem matrícula em cada
  // dia da semana — um aluno pode ter, por exemplo, turma de manhã e ensaio
  // (turno Noite) na mesma Terça. Sem separar por período, um único status
  // por dia esconderia isso: se ele faltou de manhã mas veio à noite (ou
  // vice-versa), um registro simplesmente sobrescreveria o outro no mapa por
  // data, mostrando só um dos dois de forma arbitrária.
  const periodosPorDia = useMemo(() => {
    const mapa = {};
    DIAS_SEMANA.forEach((dia) => {
      mapa[dia] = new Set();
    });
    escalaAluno.forEach((m) => {
      const dia = (m.dia_semana || '').trim();
      const periodo = turnoParaPeriodo(m.turno);
      if (mapa[dia] && periodo) mapa[dia].add(periodo);
    });
    return mapa;
  }, [escalaAluno]);

  // Status de presença por (data, período) — registros antigos sem período
  // (de antes de migrate-add-periodo-presenca.js) contam como fallback só
  // pra manhã/tarde, nunca pra noite, mesma regra de condicaoPeriodo() no
  // backend (backend/presenca.js).
  const presencaPorDataEPeriodo = useMemo(() => {
    const comPeriodo = new Map();
    const semPeriodoPorData = new Map();
    presencaSemanaAluno.forEach((p) => {
      const data = String(p.data).slice(0, 10);
      if (p.periodo) {
        comPeriodo.set(`${data}|${p.periodo}`, p.status);
      } else {
        semPeriodoPorData.set(data, p.status);
      }
    });
    return { comPeriodo, semPeriodoPorData };
  }, [presencaSemanaAluno]);

  // Um ou mais indicadores por dia (um por período com matrícula) — cada um
  // com o status daquele período específico, não um só misturando tudo.
  const indicadoresPorDia = useMemo(() => {
    const resultado = {};
    DIAS_SEMANA.forEach((dia) => {
      const data = datasDaSemana[dia];
      const periodos = Array.from(periodosPorDia[dia] || []).sort(
        (a, b) => ORDEM_PERIODOS.indexOf(a) - ORDEM_PERIODOS.indexOf(b),
      );
      resultado[dia] = periodos
        .map((periodo) => {
          const status =
            presencaPorDataEPeriodo.comPeriodo.get(`${data}|${periodo}`) ??
            (periodo !== 'noite' ? presencaPorDataEPeriodo.semPeriodoPorData.get(data) : null) ??
            null;
          return { periodo, status };
        })
        .filter((item) => item.status);
    });
    return resultado;
  }, [datasDaSemana, periodosPorDia, presencaPorDataEPeriodo]);

  const PRESENCA_INDICADOR = {
    presente: { cor: 'bg-green-500', label: 'Presente' },
    ausente: { cor: 'bg-red-500', label: 'Falta' },
    justificado: { cor: 'bg-blue-500', label: 'Justificado' },
  };

  const [alterandoPresenca, setAlterandoPresenca] = useState(null); // id do aluno em processamento

  // Um único botão, igual ao da tela de Chamada principal: mostra o status
  // que já está gravado no banco (cinza = sem chamada ainda, verde P =
  // presente, vermelho F = falta) e, ao clicar, alterna diretamente entre
  // presente e falta — grava na chamada de verdade (POST /api/presenca), não
  // uma cópia. Só aparece (ver render abaixo) quando `modoDiaSelecionado` é
  // 'hoje' — dia passado é só leitura, dia futuro nem mostra status.
  const alternarPresencaDoDia = async (alunoId) => {
    const statusAtual = presencaDoDiaPorAluno.get(alunoId) || null;
    let novoStatus = statusAtual === 'presente' ? 'ausente' : 'presente';
    const periodoAtual = turnoParaPeriodo(turmaSelecionada?.turno);

    // Se o educador está querendo marcar como PRESENTE na tela dele (onde estava sem chamada ou falta)
    if (novoStatus === 'presente') {
      try {
        setAlterandoPresenca(alunoId);
        const checkRes = await fetch(
          `${API_URL}/api/presenca/verificar?aluno_id=${alunoId}&data=${dataTurmaSelecionada}&periodo=${periodoAtual}`,
          { headers: authHeaders },
        );
        const checkBody = await checkRes.json();
        const jaPresenteNoBanco = checkBody?.data?.existe || checkBody?.existe;

        if (jaPresenteNoBanco) {
          setAlterandoPresenca(null);
          const alunoNome = roster.find((m) => m.aluno_id === alunoId)?.nome || 'o aluno';
          const labelTurno = turmaSelecionada?.turno || 'desta turma';
          const confirmarReverter = await confirm(
            `"${alunoNome}" já possui presença no turno ${labelTurno}.\n\n` +
            `Deseja atualizar a grade para carregar os dados mais recentes ou marcar falta para este aluno?`,
            {
              title: 'Presença já registrada',
              confirmText: 'Marcar falta',
              cancelText: 'Manter presença',
              extraText: '🔄 Atualizar Grade',
              tone: 'danger',
            },
          );

          if (confirmarReverter === 'extra') {
            // Recarrega a grade na hora na própria tela do educador!
            toast.info('Atualizando grade...');
            await queryClient.invalidateQueries({ queryKey: ['presencas-dia-grade'] });
            await queryClient.invalidateQueries({ queryKey: ['grade-turmas-semana'] });
            toast.success('Grade atualizada!');
            return;
          }

          if (!confirmarReverter) {
            // Cancela e atualiza os dados na tela para mostrar a presença real
            queryClient.invalidateQueries({ queryKey: ['presencas-dia-grade'] });
            toast.info('Presença mantida.');
            return;
          }

          // Se escolheu reverter, muda o status a enviar para 'ausente'
          novoStatus = 'ausente';
        }
      } catch (err) {
        console.error('Erro ao verificar duplicidade de presença na grade:', err);
      }
    }

    setAlterandoPresenca(alunoId);
    try {
      const res = await fetch(`${API_URL}/api/presenca`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        // periodo precisa vir daqui, igual à tela de Chamada (ver
        // turnoParaPeriodo) — sem isso, cada clique gravava com periodo NULL
        // e criava uma linha de presença NOVA em vez de atualizar a existente
        // (a chave única de `presenca` inclui periodo, e o MySQL nunca trata
        // dois NULLs como iguais), duplicando o registro do dia a cada toggle.
        body: JSON.stringify({
          data: dataTurmaSelecionada,
          periodo: periodoAtual,
          chamadas: [{ aluno_id: alunoId, status: novoStatus, observacao: null }],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao atualizar presença');
      }
      toast.success(novoStatus === 'presente' ? 'Marcado como presente.' : 'Situação revertida (marcado como falta).');
      queryClient.invalidateQueries({ queryKey: ['presencas-dia-grade'] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAlterandoPresenca(null);
    }
  };

  const CORES_PRESENCA_RAPIDA = {
    presente: 'bg-green-600 hover:bg-green-700 text-white',
    ausente: 'bg-red-600 hover:bg-red-700 text-white',
    // Mesmo azul do "Justificado" na tela de Chamada (AttendanceList.jsx) —
    // sem essa cor própria, um registro justificado caía no "default" cinza,
    // igual a "sem nenhum registro nesse dia" — os dois pareciam a mesma
    // coisa (um "—"), quando na verdade um TEM registro (só que não é
    // presente nem falta) e o outro não tem registro nenhum.
    justificado: 'bg-blue-600 hover:bg-blue-700 text-white',
    default: 'bg-gray-400 hover:bg-gray-500 text-white',
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-7xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="bg-white dark:bg-gray-900 p-1 rounded-xl border border-gray-200 dark:border-gray-700 inline-flex items-center">
              {TURNOS.map((turno) => (
                <button
                  key={turno}
                  onClick={() => setTurnoSelecionado(turno)}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                    turnoSelecionado === turno
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                >
                  {turno}
                </button>
              ))}
            </div>

            <div className="relative w-64">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Buscar inscrito..."
                  value={buscaAluno}
                  onChange={(e) => setBuscaAluno(e.target.value)}
                  className="w-full h-10 pl-9 pr-8 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
                {buscaAluno && (
                  <button
                    type="button"
                    onClick={() => setBuscaAluno('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5"
                    title="Limpar busca"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {buscaAluno.trim().length > 0 && (
                <div className="absolute z-30 mt-1 w-full sm:w-80 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-xl max-h-64 overflow-auto">
                  {resultadosBuscaAluno.length > 0 ? (
                    resultadosBuscaAluno.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => {
                          setAlunoSelecionado({ id: a.id, nome: a.nome });
                          setBuscaAluno('');
                        }}
                        className="w-full text-left px-4 py-2.5 border-b border-gray-50 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-between gap-2"
                      >
                        <span
                          className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                          translate="no"
                        >
                          {a.nome}
                        </span>
                        {a.turno && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                            {a.turno}
                          </span>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 text-center">
                      Nenhum inscrito encontrado.
                    </div>
                  )}
                </div>
              )}
            </div>

            {!souProfessor && (
              <div className="w-64">
                <Select
                  value={professorFiltro}
                  onChange={(e) => setProfessorFiltro(e.target.value)}
                  options={[
                    { value: '', label: 'Todos os educadores' },
                    ...professoresDisponiveis.map((p) => ({ value: p.id, label: p.nome })),
                  ]}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {souProfessor && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Suas turmas —{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100" translate="no">
                  {user.nome}
                </span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setModalPlanejamentoAberto(true)}
              title="Planejamento Semanal do Educador"
              className="h-9 px-3 rounded-xl border border-blue-200 dark:border-blue-900/60 bg-blue-50/70 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 inline-flex items-center gap-1.5 text-xs font-semibold transition-colors shadow-sm"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Planejamento da Semana</span>
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-3" />
            Carregando grade...
          </div>
        ) : professorSemVinculo ? null : (
          <Card padding="none" className="overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Grade Horária — {turnoSelecionado}
              </h2>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {turmasDoTurno.length} turma{turmasDoTurno.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 w-20">
                      Horário
                    </th>
                    {DIAS_SEMANA.map((dia) => (
                      <th
                        key={dia}
                        className={`px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 ${LARGURA_COLUNA_DIA}`}
                      >
                        {dia}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {horariosVisiveis.map((horario) => (
                    <tr
                      key={horario}
                      className="border-b border-gray-50 dark:border-gray-800 last:border-0"
                    >
                      <td className="px-3 py-2 font-medium text-gray-400 dark:text-gray-500 align-top">
                        {horario}
                      </td>
                      {DIAS_SEMANA.map((dia) => {
                        const turmas = grade[dia]?.[horario] || [];
                        return (
                          <td key={dia} className={`px-2 py-2 align-top ${LARGURA_COLUNA_DIA}`}>
                            {turmas.length === 0 ? (
                              <span className="text-gray-300 dark:text-gray-700">—</span>
                            ) : (
                              <div className="space-y-1">
                                {turmas.map((t) => {
                                  const cor = corDaAtividade(t.nome);
                                  return (
                                    <button
                                      key={t.id}
                                      onClick={() => { setTurmaSelecionada(t); setAbaModalTurma('inscritos'); }}
                                      className={`relative w-full text-left px-2 py-1 rounded-lg border transition-transform hover:scale-[1.02] flex flex-col justify-center ${ALTURA_CARD_TURMA} ${cor.bg} ${cor.border}`}
                                    >
                                      {t.total_alunos > 0 && (
                                        <span
                                          className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow-sm ring-2 ring-white dark:ring-gray-900"
                                          title={`${t.total_alunos} inscrito${t.total_alunos === 1 ? '' : 's'}`}
                                        >
                                          {t.total_alunos}
                                        </span>
                                      )}
                                      <p
                                        className={`text-xs font-medium truncate ${cor.text}`}
                                        translate="no"
                                      >
                                        {t.nome}
                                      </p>
                                      {!souProfessor && t.nome_professor && (
                                        <p
                                          className="text-[10px] text-gray-500 dark:text-gray-400 truncate"
                                          translate="no"
                                        >
                                          {t.nome_professor}
                                        </p>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {!isLoading && professorSemVinculo && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
            <p className="text-sm">Sua conta ainda não está vinculada a um cadastro de educador.</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Peça pro master vincular em Admin Usuários.
            </p>
          </div>
        )}

        {!isLoading && !professorSemVinculo && turmasDoTurno.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
            <p className="text-sm">Nenhuma turma encontrada nesse turno.</p>
          </div>
        )}
      </main>

      <Modal
        isOpen={!!turmaSelecionada}
        onClose={() => setTurmaSelecionada(null)}
        title={turmaSelecionada ? turmaSelecionada.nome : ''}
        maxWidth="md"
      >
        {turmaSelecionada && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 rounded-xl p-2.5 border border-gray-100 dark:border-gray-800">
              {turmaSelecionada.nome_professor && (
                <span translate="no">{turmaSelecionada.nome_professor}</span>
              )}
              {turmaSelecionada.nome_professor && <span>·</span>}
              <span>
                {turmaSelecionada.dia_semana}{' '}
                {dataTurmaSelecionada && `(${dataTurmaSelecionada.split('-').reverse().join('/')})`}{' '}
                {turmaSelecionada.horario} ({turmaSelecionada.turno})
              </span>
            </div>

            {/* Abas: Inscritos e Planejamento da Semana */}
            <div className="flex border-b border-gray-100 dark:border-gray-800 gap-2">
              <button
                type="button"
                onClick={() => setAbaModalTurma('inscritos')}
                className={`pb-2 px-3 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
                  abaModalTurma === 'inscritos'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                Inscritos ({roster.length})
              </button>
              <button
                type="button"
                onClick={() => setAbaModalTurma('planejamento')}
                className={`pb-2 px-3 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${
                  abaModalTurma === 'planejamento'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <BookOpen className="w-3.5 h-3.5" />
                Planejamento da Semana
                {planTurmaData?.planejado && (
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      planTurmaData.planejamento?.status === 'concluido' ? 'bg-green-500' : 'bg-amber-500'
                    }`}
                  />
                )}
              </button>
            </div>

            {abaModalTurma === 'inscritos' && (
              <>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {roster.length} Inscritos
              </p>
              {modoDiaSelecionado !== 'futuro' && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">
                    {presentesNaTurma} presentes{modoDiaSelecionado === 'hoje' ? ' hoje' : ''}
                  </p>
                </>
              )}
            </div>

            {modoDiaSelecionado === 'futuro' && (
              <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-xl px-3 py-2 -mt-2">
                Essa aula ainda não aconteceu — presença só aparece depois do dia.
              </p>
            )}
            {modoDiaSelecionado === 'passado' && (
              <p className="text-xs text-gray-400 dark:text-gray-500 -mt-2">
                Mostrando a chamada de {dataTurmaSelecionada.split('-').reverse().join('/')} (não dá
                pra alterar um dia que já passou).
              </p>
            )}

            {carregandoRoster ? (
              <div className="flex items-center justify-center py-6 text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : roster.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                Nenhum inscrito matriculado nessa turma.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-96 overflow-y-auto">
                {roster.map((m) => {
                  const statusDoDia = presencaDoDiaPorAluno.get(m.aluno_id) || null;
                  const processando = alterandoPresenca === m.aluno_id;
                  return (
                    <div
                      key={m.id}
                      className={`flex items-center justify-between px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800 ${corCardNivel(m.nivel) || 'bg-gray-50/60 dark:bg-gray-800/40'}`}
                    >
                      <button
                        onClick={() => setAlunoSelecionado({ id: m.aluno_id, nome: m.nome_aluno })}
                        className="min-w-0 text-left hover:opacity-70 transition-opacity"
                        title="Ver escala semanal do inscrito"
                      >
                        <p
                          className="text-sm text-gray-900 dark:text-gray-100 truncate underline decoration-dotted decoration-gray-300 dark:decoration-gray-600 underline-offset-2"
                          translate="no"
                        >
                          <span className="inline-flex items-center gap-1.5 flex-wrap">
                            <span>{m.nome_aluno}</span>
                            <AlunoBadges aluno={{ lider_servidor: m.lider_servidor, laudos: m.laudos, cuidados_medicos: m.cuidados_medicos }} compact />
                          </span>
                        </p>
                        {m.transporte && (
                          <p
                            className="text-[10px] text-gray-400 dark:text-gray-500 truncate"
                            translate="no"
                          >
                            {m.transporte}
                          </p>
                        )}
                      </button>
                      {modoDiaSelecionado === 'futuro' ? null : modoDiaSelecionado === 'passado' ? (
                        // Dia passado: só leitura — mesma cor de sempre, sem onClick,
                        // sem hover — não dá pra reescrever a chamada de um dia que já
                        // aconteceu por aqui (isso é papel da tela de Chamada de verdade).
                        <span
                          title={
                            statusDoDia === 'presente'
                              ? 'Presente'
                              : statusDoDia === 'ausente'
                                ? 'Falta'
                                : statusDoDia === 'justificado'
                                  ? 'Falta justificada'
                                  : 'Sem registro nesse dia'
                          }
                          className={`flex-shrink-0 w-8 h-8 rounded-lg text-xs font-bold flex items-center justify-center cursor-default ${
                            CORES_PRESENCA_RAPIDA[statusDoDia] || CORES_PRESENCA_RAPIDA.default
                          }`}
                        >
                          {statusDoDia === 'presente'
                            ? 'P'
                            : statusDoDia === 'ausente'
                              ? 'F'
                              : statusDoDia === 'justificado'
                                ? 'J'
                                : '—'}
                        </span>
                      ) : (
                        <button
                          onClick={() => alternarPresencaDoDia(m.aluno_id)}
                          disabled={processando || !podeEditar}
                          title={
                            !podeEditar
                              ? 'Sem permissão para editar presença'
                              : statusDoDia === 'presente'
                                ? 'Presente — clique pra marcar falta'
                                : statusDoDia === 'ausente'
                                  ? 'Falta — clique pra marcar presente'
                                  : statusDoDia === 'justificado'
                                    ? 'Falta justificada — clique pra marcar presente'
                                    : 'Sem chamada ainda — clique pra marcar presente'
                          }
                          className={`flex-shrink-0 w-8 h-8 rounded-lg text-xs font-bold flex items-center justify-center transition-colors disabled:opacity-60 ${
                            CORES_PRESENCA_RAPIDA[statusDoDia] || CORES_PRESENCA_RAPIDA.default
                          }`}
                        >
                          {processando ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : statusDoDia === 'presente' ? (
                            'P'
                          ) : statusDoDia === 'ausente' ? (
                            'F'
                          ) : statusDoDia === 'justificado' ? (
                            'J'
                          ) : (
                            ''
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
              </>
            )}

            {/* Conteúdo da Aba: Planejamento da Semana */}
            {abaModalTurma === 'planejamento' && (
              <div className="space-y-4 pt-1">
                {carregandoPlanTurma ? (
                  <div className="py-8 text-center text-gray-400 text-xs flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    Consultando planejamento da semana...
                  </div>
                ) : planTurmaData?.planejado && planTurmaData?.planejamento ? (
                  <div className="space-y-3">
                    {/* Barra de Status e Ação Rápida */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Status:</span>
                        <Badge tone={planTurmaData.planejamento.status === 'concluido' ? 'success' : 'warning'}>
                          {planTurmaData.planejamento.status === 'concluido' ? (
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Concluído (Feito)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Pendente
                            </span>
                          )}
                        </Badge>
                      </div>

                      <Button
                        variant={planTurmaData.planejamento.status === 'concluido' ? 'secondary' : 'success'}
                        size="sm"
                        icon={planTurmaData.planejamento.status === 'concluido' ? Clock : CheckCircle2}
                        onClick={() => alternarStatusPlanejamento(planTurmaData.planejamento)}
                      >
                        {planTurmaData.planejamento.status === 'concluido' ? 'Reabrir' : 'Marcar como Feito'}
                      </Button>
                    </div>

                    {/* Aviso Teoria Compartilhada */}
                    {planTurmaData.planejamento.eh_compartilhado === 1 && (
                      <div className="p-2.5 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-xl flex items-center gap-2 text-xs text-purple-800 dark:text-purple-300">
                        <Users className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                        <span>
                          <strong>Teoria Compartilhada:</strong> Conteúdo sincronizado entre todos os professores desta disciplina.
                        </span>
                      </div>
                    )}

                    {/* Conteúdo Planejado */}
                    <div className="space-y-1">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" /> Conteúdo Planejado
                      </div>
                      <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                        {planTurmaData.planejamento.conteudo}
                      </div>
                    </div>

                    {/* Anotações Pós-aula */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                          <MessageSquare className="w-3.5 h-3.5" /> Anotações / Realizado na Aula
                        </div>
                        {!editandoObsTurma && (
                          <button
                            type="button"
                            onClick={() => setEditandoObsTurma(true)}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                          >
                            <Pencil className="w-3 h-3" /> {planTurmaData.planejamento.observacoes ? 'Editar anotação' : '+ Adicionar anotação'}
                          </button>
                        )}
                      </div>

                      {editandoObsTurma ? (
                        <div className="space-y-2">
                          <textarea
                            rows={3}
                            className="w-full rounded-xl border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 p-2.5 text-xs text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Adicione observações da aula (ex: turma avançou bem, faltou terminar página 10)..."
                            value={textoObsTurma}
                            onChange={(e) => setTextoObsTurma(e.target.value)}
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setTextoObsTurma(planTurmaData.planejamento.observacoes || '');
                                setEditandoObsTurma(false);
                              }}
                              disabled={salvandoObsTurma}
                            >
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => salvarObsTurma(planTurmaData.planejamento.id)}
                              disabled={salvandoObsTurma}
                              icon={salvandoObsTurma ? Loader2 : CheckCircle2}
                            >
                              Salvar
                            </Button>
                          </div>
                        </div>
                      ) : planTurmaData.planejamento.observacoes ? (
                        <div className="text-xs text-blue-900 dark:text-blue-200 whitespace-pre-wrap leading-relaxed bg-blue-50/60 dark:bg-blue-950/40 p-2.5 rounded-xl border border-blue-100 dark:border-blue-900/40">
                          {planTurmaData.planejamento.observacoes}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                          Nenhuma anotação pós-aula registrada ainda.
                        </p>
                      )}
                    </div>

                    {/* Rodapé com link */}
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs text-gray-400">
                      <span>
                        {planTurmaData.planejamento.criador_nome && `Por: ${planTurmaData.planejamento.criador_nome}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setTurmaSelecionada(null);
                          navigate('/planejamento');
                        }}
                        className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-medium"
                      >
                        <ExternalLink className="w-3 h-3" /> Ver histórico completo
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-2xl space-y-3">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                          Sem planejamento registrado para esta semana
                        </h4>
                        <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
                          Cadastre o conteúdo planejado para a disciplina "{turmaSelecionada.nome}" diretamente aqui:
                        </p>
                      </div>
                    </div>

                    <form onSubmit={criarPlanejamentoTurma} className="space-y-2 pt-1">
                      <textarea
                        rows={3}
                        required
                        className="w-full rounded-xl border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 p-2.5 text-xs text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: Leitura de partituras, exercícios na clave de sol, páginas 10 a 14..."
                        value={novoConteudoTurma}
                        onChange={(e) => setNovoConteudoTurma(e.target.value)}
                      />
                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          variant="primary"
                          size="sm"
                          disabled={salvandoNovoPlanoTurma}
                          icon={salvandoNovoPlanoTurma ? Loader2 : CheckCircle2}
                        >
                          Salvar Planejamento
                        </Button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!alunoSelecionado}
        onClose={() => setAlunoSelecionado(null)}
        title={alunoSelecionado ? `Escala semanal — ${alunoSelecionado.nome}` : ''}
        maxWidth="xl"
      >
        {alunoSelecionado &&
          (carregandoEscala ? (
            <div className="flex items-center justify-center py-6 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto -mx-3">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 w-16">
                      Horário
                    </th>
                    {DIAS_SEMANA.map((dia) => {
                      const indicadores = indicadoresPorDia[dia] || [];
                      return (
                        <th
                          key={dia}
                          className={`px-2 py-2 text-left font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 ${LARGURA_COLUNA_DIA}`}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {indicadores.length > 0 && (
                              <span className="inline-flex items-center gap-0.5">
                                {indicadores.map(({ periodo, status }) => (
                                  <span
                                    key={periodo}
                                    className={`w-1.5 h-1.5 rounded-full ${PRESENCA_INDICADOR[status].cor}`}
                                    title={`${dia} (${LABEL_PERIODO[periodo]}): ${PRESENCA_INDICADOR[status].label}`}
                                  />
                                ))}
                              </span>
                            )}
                            {dia}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {horariosVisiveisEscala.map((horario) => (
                    <tr
                      key={horario}
                      className="border-b border-gray-50 dark:border-gray-800 last:border-0"
                    >
                      <td className="px-3 py-2 font-medium text-gray-400 dark:text-gray-500 align-top">
                        {horario}
                      </td>
                      {DIAS_SEMANA.map((dia) => {
                        const turmas = escalaAlunoGrid[dia]?.[horario] || [];
                        return (
                          <td key={dia} className={`px-1.5 py-2 align-top ${LARGURA_COLUNA_DIA}`}>
                            {turmas.length === 0 ? (
                              <span className="text-gray-300 dark:text-gray-700">—</span>
                            ) : (
                              <div className="space-y-1">
                                {turmas.map((m) => {
                                  const cor = corDaAtividade(m.nome_atividade);
                                  return (
                                    <div
                                      key={m.id}
                                      className={`px-2 py-1 rounded-lg border flex flex-col justify-center ${ALTURA_CARD_TURMA} ${cor.bg} ${cor.border}`}
                                    >
                                      <p
                                        className={`text-xs font-medium truncate ${cor.text}`}
                                        translate="no"
                                      >
                                        {m.nome_atividade || 'Turma'}
                                      </p>
                                      {m.nome_professor && (
                                        <p
                                          className="text-[10px] text-gray-500 dark:text-gray-400 truncate"
                                          translate="no"
                                        >
                                          {m.nome_professor}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </Modal>

      <PlanejamentoGradeModal
        isOpen={modalPlanejamentoAberto}
        onClose={() => setModalPlanejamentoAberto(false)}
        dataAtual={hoje}
        idProfessor={idProfessorEfetivo}
        disciplinaInicial={turmaSelecionada?.nome || ''}
      />
    </div>
  );
}

export default Grade;
